import useStyles from 'isomorphic-style-loader/useStyles';
import { Layers3 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Label } from 'semantic-ui-react';

import type { Filter, PaginationSort } from '@models';
import { extractSearchPages } from '@store/search';
import { ComponentLoader } from '@components/component-loader';
import EmptyPlaceholder from '@components/empty-placeholder';
import Err from '@components/err';
import { hasActiveFilters } from '@components/filters/filter-utils';
import ListActionsHeader from '@components/list-actions-header';
import Page from '@components/page';
import {
  useInfiniteScrollOptions,
  useTableSortChange,
} from '@components/table';
import {
  VERSION_STATUS_LABELS,
  VEX_STATUS_FILTER_OPTIONS,
} from '@features/applications/applications.const';
import {
  VulnerabilityAnnotationDeleteModal,
  VulnerabilityAnnotationUpsertModal,
} from '@features/applications/common/vulnerability-annotations';

import {
  DEFAULT_IMPACTED_APP_VERSIONS_FILTERS,
  DEFAULT_IMPACTED_APP_VERSIONS_SORT,
  useGetVulnerabilityImpactStatusQuery,
  useGetImpactedAppVersionsInfiniteQuery,
  useVulnerabilityLookupQuery,
} from '../quick-search.api';
import { noVulnerabilityDataMessage } from '../quick-search.utils';

import { ImpactedAppVersionsTable } from './impacted-app-versions-table';
import { VulnerabilitySearchResultsPageHeader } from './vulnerability-search-results-page-header';
import type { ImpactedAppVersionsFilters } from './vulnerability-search.models';
import s from './vulnerability-search-results-page.scss';

export interface VulnerabilitySearchResultsPageProps {
  vulnerabilityId: string;
}

const IMPACTED_APP_VERSIONS_FILTERS: Filter<
  keyof ImpactedAppVersionsFilters
>[] = [
  { key: 'applicationName', name: 'Application Name', typeName: 'String' },
  { key: 'versionName', name: 'Version Name', typeName: 'String' },
  { key: 'contactName', name: 'Contact Name', typeName: 'String' },
  { key: 'contactEmail', name: 'Contact Email', typeName: 'String' },
  {
    key: 'status',
    name: 'Status',
    typeName: 'EnumList',
    enumValues: Object.entries(VERSION_STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  },
  { key: 'releaseDate', name: 'Release Date', typeName: 'DateRange' },
  { key: 'hasFix', name: 'Has Fix', typeName: 'Boolean', data: true },
  {
    key: 'affectedPackagesMin',
    name: 'Min Affected Packages',
    typeName: 'Int',
    validationOptions: { minValue: 0 },
  },
  {
    key: 'affectedPackagesMax',
    name: 'Max Affected Packages',
    typeName: 'Int',
    validationOptions: { minValue: 0 },
  },
  {
    key: 'annotationStatus',
    name: 'Annotation Status',
    typeName: 'EnumList',
    enumValues: VEX_STATUS_FILTER_OPTIONS,
  },
];

/**
 * Quick Search results page for a specific vulnerability. Loads the
 * vulnerability metadata, then the affected application versions — whose
 * presence drives the header's impact status and populates the table.
 */
export const VulnerabilitySearchResultsPage = ({
  vulnerabilityId,
}: VulnerabilitySearchResultsPageProps) => {
  useStyles(s);

  const [sort, setSort] = useState<PaginationSort>(
    DEFAULT_IMPACTED_APP_VERSIONS_SORT,
  );
  const [filters, setFilters] = useState<ImpactedAppVersionsFilters>(
    DEFAULT_IMPACTED_APP_VERSIONS_FILTERS,
  );

  const {
    data: lookup,
    isLoading,
    error,
  } = useVulnerabilityLookupQuery(vulnerabilityId);

  const vulnerabilities = lookup?.vulnerabilities ?? [];

  const skipSearch = vulnerabilities.length !== 1;

  // Fired alongside the list so the pill reflects the whole deployment, not
  // the filtered results.
  const impactStatus = useGetVulnerabilityImpactStatusQuery(vulnerabilityId, {
    skip: skipSearch,
  });

  const impact = useGetImpactedAppVersionsInfiniteQuery(
    { vulnerabilityId, sort, filters },
    { skip: skipSearch },
  );

  const items = useMemo(() => extractSearchPages(impact.data), [impact.data]);

  const infinite = useInfiniteScrollOptions(
    impact,
    'Loading application versions...',
  );

  const onSortChange = useTableSortChange(setSort);

  if (isLoading) {
    return (
      <Page>
        <Page.Content className={s.loader}>
          <ComponentLoader
            active
            inline="centered"
            size="huge"
            content="Loading vulnerability"
          />
        </Page.Content>
      </Page>
    );
  }

  // A well-formed lookup resolves to exactly one vulnerability; a genuine
  // request error surfaces the error pane, while zero (no match) and more than
  // one (ambiguous) both surface the same no-results state as the search modal.
  if (error || vulnerabilities.length !== 1) {
    return (
      <Page skeleton>
        {error ? (
          <Err error={error} noBorder />
        ) : (
          <EmptyPlaceholder
            illustration={<img src="/img/robot_notfound.svg" alt="" />}
            title="No results found"
            docsMessage={noVulnerabilityDataMessage(
              vulnerabilityId,
              lookup?.grypedbMetadata.builtAt,
            )}
          />
        )}
      </Page>
    );
  }

  // Surface the impact pill only once its (filter-independent) call resolves;
  // never while the "Assessing Impact..." loader is up or the results errored.
  const impacted =
    impact.isLoading ||
    impact.isError ||
    impactStatus.isLoading ||
    impactStatus.isError
      ? undefined
      : impactStatus.data?.impacted;

  const impactErrorStatus =
    impact.error && 'status' in impact.error ? impact.error.status : undefined;

  return (
    <Page>
      <VulnerabilitySearchResultsPageHeader
        vulnerability={vulnerabilities[0]}
        impacted={impacted}
        builtAt={lookup?.grypedbMetadata.builtAt}
      />
      <Page.Content className={impact.isLoading ? s.loader : undefined}>
        {impact.isLoading ? (
          <ComponentLoader
            active
            inline="centered"
            size="huge"
            content="Assessing Impact..."
          />
        ) : // A 403 replaces the whole section; every other error keeps the
        // section header and renders inside the table area.
        impactErrorStatus === 403 ? (
          <EmptyPlaceholder
            illustration={<img src="/img/robot_notfound.svg" alt="" />}
            title="Not permitted to view these results"
            docsMessage="Your account doesn't have permission to see results for this search. Ask your administrator for access if you think you should have it."
          />
        ) : (
          <>
            <ListActionsHeader<ImpactedAppVersionsFilters>
              defaultFilterValues={DEFAULT_IMPACTED_APP_VERSIONS_FILTERS}
              filterOptions={IMPACTED_APP_VERSIONS_FILTERS}
              filterValues={filters}
              icon={<Layers3 color="var(--sui-color-teal)" />}
              onFilterChange={setFilters}
              title={
                <span className={s.sectionTitle}>
                  Impacted Application Versions
                  {!impact.isError && (
                    <Label circular className={s.count}>
                      {/* Cursor pagination gives no total, so append `+` while
                          more pages remain to be fetched. */}
                      {items.length}
                      {impact.hasNextPage ? '+' : ''}
                    </Label>
                  )}
                </span>
              }
            />
            <ImpactedAppVersionsTable
              vulnerabilityId={vulnerabilityId}
              builtAt={lookup?.grypedbMetadata.builtAt}
              data={items}
              error={impact.error}
              // With nothing impacted, filters can't be what's hiding rows.
              filtering={
                hasActiveFilters(filters) && !!impactStatus.data?.impacted
              }
              infinite={infinite}
              loading={impact.isFetching && !impact.isFetchingNextPage}
              onSortChange={onSortChange}
              sort={sort}
            />
          </>
        )}
      </Page.Content>

      <VulnerabilityAnnotationDeleteModal />
      <VulnerabilityAnnotationUpsertModal />
    </Page>
  );
};

export default VulnerabilitySearchResultsPage;
